(() => {
  if (window.AWPackMerchandising?.version) return;

  const VERSION = '1.3.2';
  const labels = {
    magazines: 'Chargeurs',
    power: 'Alimentation',
    consumables: 'Consommables',
    hpa: 'Circuit HPA',
    mounting: 'Optique & montage',
    upgrade: 'Upgrade',
    other: 'Autres'
  };

  const normalize = (value = '') => String(value)
    .replace(/[₂²]/g, '2')
    .replace(/[₀⁰]/g, '0')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

  const isBackorder = (card) => card?.dataset?.awBackorder === 'true';

  const logisticsRank = (card) => {
    const structuredRank = Number(card?.dataset?.awLogisticsPriority);
    if (Number.isFinite(structuredRank) && structuredRank > 0) return structuredRank;

    const logistics = card?.querySelector('.aw-compatible-card__logistics');
    if (!logistics) return 60;
    const label = normalize(logistics.querySelector('strong')?.textContent || '');
    const delay = normalize(logistics.querySelector('small')?.textContent || '');

    if (label === 'en-stock' || delay.includes('1-a-6-jours')) return 10;
    if (delay.includes('1-a-4-jours')) return 20;
    if (delay.includes('1-a-2-semaines')) return 40;
    if (label.includes('preparation-atelier')) return 45;
    if (delay.includes('1-a-4-semaines')) return 50;
    if (label.includes('indisponible')) return 90;
    if (label === 'disponible') return 30;
    return 60;
  };

  const availabilityTieBonus = (rank) => {
    if (rank <= 10) return 5;
    if (rank <= 20) return 4;
    if (rank <= 30) return 3;
    if (rank <= 40) return 2;
    if (rank <= 50) return 1;
    return 0;
  };

  const track = (name, data = {}) => {
    try { window.AWAnalytics?.track?.(name, data); } catch (_) {}
  };

  const productCompatibilityData = (root) => {
    const node = document.querySelector('[data-aw-product-compatibility]');
    if (!node) return null;
    try {
      const data = JSON.parse(node.textContent || '{}');
      const currentProductId = String(root.dataset.currentProductId || '');
      if (currentProductId && data.productId && String(data.productId) !== currentProductId) return null;
      return data;
    } catch (_) {
      return null;
    }
  };

  const productPlatforms = (root) => {
    const data = productCompatibilityData(root);
    const values = Array.isArray(data?.platforms) ? data.platforms : [];
    const platforms = [...new Set(values.map(normalize).filter(Boolean))];
    const fallback = normalize(root.dataset.defaultPlatform || '');
    if (!platforms.length && fallback) platforms.push(fallback);
    return platforms;
  };

  const installAvailabilityRanking = (root) => {
    if (!root) return false;
    const instance = root.awCatalogCompatibility;
    if (!instance || typeof instance.score !== 'function') return false;
    if (instance.__awAvailabilityRanking === true) return false;

    const baseScore = instance.score.bind(instance);
    instance.score = (card) => {
      const score = baseScore(card);
      if (typeof score !== 'number' || !Number.isFinite(score) || score < 0) return score;
      if (isBackorder(card)) return score;
      return score + availabilityTieBonus(logisticsRank(card));
    };

    instance.__awAvailabilityRanking = true;
    root.dataset.awAvailabilitySensitive = 'true';
    root.dataset.awBackorderAware = 'true';
    root.dataset.awAvailabilityPolicy = 'compatibility-first-structured-logistics-tiebreak';
    root.dataset.awAvailabilityScoring = 'verified-equivalent-availability-v3';

    track('pack_availability_ranking_applied', {
      product_context: root.dataset.awProductContext || '',
      pack_mode: root.dataset.awPackMode || '',
      policy: root.dataset.awAvailabilityPolicy
    });
    return true;
  };

  const installProductPlatformGuard = (root) => {
    if (!root) return false;
    const instance = root.awCatalogCompatibility;
    if (!instance) return false;
    const availabilityInstalled = installAvailabilityRanking(root);
    if (root.dataset.awPackMode === 'ready-to-play') {
      if (availabilityInstalled) {
        instance.render?.();
        instance.ensureResults?.();
      }
      return true;
    }
    if (instance.__awProductPlatformGuard === true) return true;

    const supported = productPlatforms(root);
    const fallback = normalize(root.dataset.defaultPlatform || supported[0] || '');
    if (!supported.length || !fallback) return false;

    const choosePlatform = (value = '') => {
      const candidate = normalize(value);
      return candidate && supported.includes(candidate) ? candidate : fallback;
    };

    instance.readConfig = () => {
      const stored = typeof instance.read === 'function'
        ? instance.read(root.dataset.storageKey || 'aw_selected_platform')
        : '';
      return {
        platform: choosePlatform(stored),
        manufacturer: '',
        type: ''
      };
    };

    instance.syncControls = () => {
      const source = document.querySelector('[data-aw-compatibility]');
      const selected = source?.querySelector('[data-aw-platform-select]')?.value || '';
      instance.config = {
        platform: choosePlatform(selected || instance.config?.platform || fallback),
        manufacturer: '',
        type: ''
      };
    };

    instance.apply = (detail = {}) => {
      const previousPlatform = typeof instance.platformHandle === 'function'
        ? instance.platformHandle()
        : normalize(instance.config?.platform || '');
      const requested = detail.platform !== undefined
        ? detail.platform
        : (instance.config?.platform || fallback);
      const nextPlatform = choosePlatform(requested);

      instance.config = {
        platform: nextPlatform,
        manufacturer: '',
        type: ''
      };

      if (nextPlatform !== previousPlatform) instance.loadedPlatform = '';
      instance.render?.();
      instance.ensureResults?.();
    };

    instance.config = instance.readConfig();
    instance.loadedPlatform = '';
    instance.__awProductPlatformGuard = true;
    root.dataset.awContextPolicy = 'product-platform-authoritative';
    root.dataset.awSupportedPlatforms = supported.join(',');
    instance.render?.();
    instance.ensureResults?.();

    track('pack_context_guard_applied', {
      product_context: root.dataset.awProductContext || '',
      pack_mode: root.dataset.awPackMode || '',
      platform: instance.config.platform || '',
      supported_platforms: supported.join(',')
    });
    return true;
  };

  class PackMerch {
    constructor(root) {
      if (root.dataset.awPackMerchReady === 'true') return;
      this.root = root;
      this.grid = root.querySelector('[data-aw-products-grid]');
      this.nav = root.querySelector('[data-aw-pack-categories]');
      if (!this.grid || !this.nav) return;
      this.active = 'all';
      this.timer = 0;
      this.observer = new MutationObserver(() => this.schedule());
      this.observer.observe(this.grid, { childList: true });
      ['aw:platform-changed', 'aw:compatibility-changed', 'aw:platform-reset'].forEach((name) =>
        addEventListener(name, () => this.schedule())
      );
      this.nav.addEventListener('click', (event) => {
        const button = event.target.closest('[data-aw-pack-category]');
        if (!button) return;
        this.active = button.dataset.awPackCategory || 'all';
        this.apply();
        this.paint();
        track('pack_category_selected', {
          category: this.active,
          product_context: this.root.dataset.awProductContext || '',
          pack_mode: this.root.dataset.awPackMode || '',
          platform: this.root.awCatalogCompatibility?.config?.platform || this.root.dataset.defaultPlatform || ''
        });
      });
      this.root.dataset.awPackMerchReady = 'true';
      this.schedule();
    }

    schedule() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.refresh(), 80);
    }

    restore() {
      this.grid.querySelectorAll('[data-aw-pack-merch-hidden="true"]').forEach((card) => {
        card.hidden = false;
        delete card.dataset.awPackMerchHidden;
      });
    }

    refresh() {
      installProductPlatformGuard(this.root);
      this.restore();
      const eligible = [...this.grid.querySelectorAll('[data-aw-product-card]')]
        .filter((card) => !card.hidden && card.dataset.awEligible === 'true');
      const counts = {};
      eligible.forEach((card) => {
        const key = card.dataset.awMerchCategory || 'other';
        counts[key] = (counts[key] || 0) + 1;
      });
      const keys = Object.keys(counts);
      if (this.active !== 'all' && !counts[this.active]) this.active = 'all';
      this.nav.innerHTML = '';
      if (keys.length) {
        this.nav.appendChild(this.button('all', 'Tout', eligible.length));
        keys
          .sort((a, b) => (labels[a] || a).localeCompare(labels[b] || b, 'fr'))
          .forEach((key) => this.nav.appendChild(this.button(key, labels[key] || key, counts[key])));
        this.nav.hidden = false;
      } else {
        this.nav.hidden = true;
      }
      this.apply();
      this.paint();
    }

    button(key, label, count) {
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.awPackCategory = key;
      button.textContent = `${label} · ${count}`;
      return button;
    }

    apply() {
      this.restore();
      if (this.active === 'all') return;
      this.grid.querySelectorAll('[data-aw-product-card]').forEach((card) => {
        if (!card.hidden && (card.dataset.awMerchCategory || 'other') !== this.active) {
          card.hidden = true;
          card.dataset.awPackMerchHidden = 'true';
          const box = card.querySelector('[data-aw-product-checkbox]');
          if (box) box.checked = false;
        }
      });
      this.root.awCatalogCompatibility?.updateSelection?.();
    }

    paint() {
      this.nav.querySelectorAll('[data-aw-pack-category]').forEach((button) => {
        const active = button.dataset.awPackCategory === this.active;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll('[data-aw-compatible-products]').forEach((root) => {
      if (!installProductPlatformGuard(root)) {
        window.setTimeout(() => installProductPlatformGuard(root), 0);
        window.setTimeout(() => installProductPlatformGuard(root), 120);
      }
      if (!root.awPackMerchandising) root.awPackMerchandising = new PackMerch(root);
    });
  };

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  window.AWPackMerchandising = {
    version: VERSION,
    init,
    installAvailabilityRanking,
    installProductPlatformGuard,
    policy: 'compatibility-first-structured-logistics-tiebreak'
  };
})();
